import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import BylineWithHeadshot from '@components/BylineWithHeadshot';
import LazyImgixImage from '@components/LazyImgixImage';
import ProExclusiveLogo from '@components/ProExclusiveLogo';
import ProjectTitle from '@components/ProjectTitle';
import Media from '@components/pages/Project/Layouts/Database/Media';
import useDatawrapperResize from '@hooks/useDatawrapperResize';
import { Project, ProjectMedia } from '@interfaces/projects';
import { ACCESS_PRO } from '@src/constants';
import { getImgixSrc } from '@utils/images';

type TopSectionProps = Pick<
  Project,
  'accessLevel' | 'authors' | 'description' | 'title' | 'titleCallout'
> & {
  mediaSourcePath: string;
  projectMedia?: ProjectMedia[];
};

const TopSection = ({
  accessLevel,
  authors,
  description,
  mediaSourcePath,
  projectMedia,
  title,
  titleCallout,
}: TopSectionProps): JSX.Element => {
  useDatawrapperResize();

  return (
    <div className="mx-auto max-w-5xl px-16">
      {accessLevel >= ACCESS_PRO && <ProExclusiveLogo />}
      <ProjectTitle className="!mb-8" title={title} titleCallout={titleCallout} />
      {authors?.length > 0 && (
        <div className="mb-16 flex justify-center">
          <BylineWithHeadshot
            authors={authors}
            className="font-suisse-screen text-byline-sm text-primary-black sm:text-byline-lg"
          />
        </div>
      )}
      <div className="flex flex-col items-center justify-center gap-16 xs:flex-row xs:gap-32">
        <div className="flex flex-col items-center justify-center gap-16 xs:flex-row xs:gap-32">
          <div className="flex items-center justify-center gap-8">
            <p className="text-center font-suisse-intl text-body-sm font-light">
              In data partnership with
            </p>
            <div className="flex items-center justify-center">
              <a
                className="track-click flex"
                data-name="IPO Tracker Caplight Logo"
                href="https://www.caplight.com"
                rel="noreferrer sponsored"
                target="_blank"
              >
                <LazyImgixImage
                  alt="Caplight Logo"
                  className="h-24 dark:bg-white dark:p-4"
                  src={getImgixSrc({
                    path: 'projects/ipo-tracker/caplight-logo.svg?h=48',
                    isEnvDependent: false,
                  })}
                  visibleByDefault
                />
              </a>
            </div>
          </div>
        </div>
      </div>
      {projectMedia?.length > 0 ? (
        <div className="flex flex-col flex-wrap items-center justify-between gap-40 md:flex-row md:items-start">
          <div className="my-18 w-full flex-grow md:mt-64 md:w-5/12">
            {ReactHtmlParser(description)}
          </div>
          {projectMedia.map((media) => (
            <Media key={media.id} media={media} mediaSourcePath={mediaSourcePath} />
          ))}
        </div>
      ) : (
        <div className="my-18 mx-auto w-full md:mt-64">{ReactHtmlParser(description)}</div>
      )}
    </div>
  );
};

export default TopSection;
